import { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  ShoppingBag, Truck, Star, ArrowLeft, Loader2, CreditCard,
  Trophy, Calendar, Package, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${API}/shop/products/${productId}`);
        setProduct(res.data);
      } catch {
        toast.error("Produit introuvable");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    const poll = async (attempts = 0) => {
      if (attempts >= 5) { setPaymentStatus("timeout"); return; }
      try {
        const res = await axios.get(`${API}/shop/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setPaymentStatus("paid");
          toast.success("Paiement réussi ! Votre commande est confirmée.");
          return;
        }
        if (res.data.status === "expired") { setPaymentStatus("expired"); return; }
        setTimeout(() => poll(attempts + 1), 2000);
      } catch {
        setTimeout(() => poll(attempts + 1), 2000);
      }
    };
    setPaymentStatus("checking");
    poll();
  }, [sessionId]);

  const handleStripeCheckout = async () => {
    if (!token) { toast.error("Connectez-vous pour acheter"); return; }
    setPurchasing(true);
    try {
      const form = new FormData();
      form.append("product_id", productId);
      form.append("origin_url", window.location.origin);
      const res = await axios.post(`${API}/shop/checkout`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de paiement");
      setPurchasing(false);
    }
  };

  const handlePointsPurchase = async () => {
    if (!token) { toast.error("Connectez-vous pour acheter"); return; }
    setPurchasing(true);
    try {
      const form = new FormData();
      form.append("product_id", productId);
      const res = await axios.post(`${API}/shop/purchase-points`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      navigate("/shop/orders");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }
  if (!product) {
    return <div className="text-center py-20"><p className="text-muted-foreground">Produit introuvable</p></div>;
  }

  const estimatedDate = new Date(Date.now() + product.delivery_days * 86400000)
    .toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="min-h-screen pb-24 px-4 pt-4" data-testid="product-detail-page">
      <div className="max-w-3xl mx-auto space-y-5">
        <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-charcoal">
          <ArrowLeft className="w-4 h-4" /> Retour à la boutique
        </Link>

        {/* Payment Status Banner */}
        {paymentStatus === "checking" && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              <p className="text-sm text-blue-700">Vérification du paiement en cours...</p>
            </CardContent>
          </Card>
        )}
        {paymentStatus === "paid" && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Package className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-700">Paiement confirmé !</p>
                <p className="text-xs text-green-600">Livraison estimée le {estimatedDate}</p>
              </div>
              <Link to="/shop/orders" className="ml-auto">
                <Button size="sm" variant="outline" className="rounded-full text-xs">Mes commandes</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Product Image */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Card className="overflow-hidden">
            <div className="aspect-[16/10] bg-muted relative">
              <img src={product.featured_image} alt={product.name} className="w-full h-full object-cover" />
              <Badge className="absolute top-3 left-3 bg-primary text-white">{product.points_price} points</Badge>
            </div>
          </Card>
        </motion.div>

        {/* Gallery */}
        {product.gallery_images?.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {product.gallery_images.map((img, i) => (
              <img key={i} src={img} alt={`${product.name} ${i + 1}`} className="w-20 h-20 object-cover rounded-lg flex-shrink-0" />
            ))}
          </div>
        )}

        {/* YouTube Video */}
        {product.youtube_url && (
          <Card className="overflow-hidden">
            <div className="aspect-video">
              <iframe
                className="w-full h-full"
                src={product.youtube_url.replace("watch?v=", "embed/").split("&")[0]}
                title="Vidéo démo"
                allow="autoplay; encrypted-media"
                allowFullScreen
                frameBorder="0"
              />
            </div>
          </Card>
        )}

        {/* Product Info */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div>
              <h1 className="font-heading text-2xl font-bold text-charcoal" data-testid="product-name">{product.name}</h1>
              <Badge variant="outline" className="mt-1">{product.category}</Badge>
            </div>
            <p className="text-muted-foreground leading-relaxed">{product.description}</p>

            <div className="flex items-center gap-4 py-3 border-y">
              <div>
                <p className="text-3xl font-bold text-primary">{product.price.toFixed(2)}€</p>
                <p className="text-sm text-muted-foreground">ou <strong className="text-primary">{product.points_price} points</strong></p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-sm text-muted-foreground flex items-center gap-1 justify-end">
                  <Truck className="w-4 h-4" /> Livraison en {product.delivery_days} jours
                </p>
                <p className="text-sm font-medium text-charcoal flex items-center gap-1 justify-end">
                  <Calendar className="w-4 h-4" /> Réception estimée : {estimatedDate}
                </p>
              </div>
            </div>

            {/* Purchase Buttons */}
            {!paymentStatus || paymentStatus === "timeout" ? (
              <div className="space-y-4 pt-2">
                <Button
                  onClick={(e) => { e.stopPropagation(); handleStripeCheckout(); }}
                  disabled={purchasing}
                  className="w-full rounded-full gap-2 h-14 text-base"
                  data-testid="buy-stripe-btn"
                >
                  {purchasing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                  Payer {product.price.toFixed(2)}€ par carte
                </Button>
                <Button
                  onClick={(e) => { e.stopPropagation(); handlePointsPurchase(); }}
                  disabled={purchasing}
                  variant="outline"
                  className="w-full rounded-full gap-2 h-14 text-base border-primary text-primary hover:bg-primary/5"
                  data-testid="buy-points-btn"
                >
                  {purchasing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-5 h-5" />}
                  Payer avec {product.points_price} points
                </Button>
                {user && (
                  <p className="text-center text-sm text-muted-foreground">
                    Vous avez <strong className="text-primary">{user.total_points || 0} points</strong>
                  </p>
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductDetailPage;
